import { useEffect, useRef } from 'react';
import { useInvalidateSetupGuide, useSetupGuide } from './hook';
import { updateSetupGuide } from './api';
import type { SetupGuide } from './types';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { useDomainsOptional, type DomainsList } from '@/services/db/domain/queries';
import { isSendingVerified } from '@/services/db/domain/entity';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import type { WorkspaceEnvironment } from '@/services/db/environment/entity';
import { useIntegrationStatusOptional } from '@/services/db/setup-guide/queries';
import { DeliverySettings, useSampleWorkspace } from '@/features/workspaces';
import { useAuth } from '@/services/auth';
import { VerificationState } from '@/services/auth/types';

export function useWorkspaceEnvironment() {
  const { data } = useEnvironmentOptional();

  return data?.environment ?? null;
}

export function useWorkspaceSetupGuide() {
  const { setupGuideQuery, workspaceId } = useSetupGuide();

  return {
    workspaceId,
    setupGuide: setupGuideQuery.data?.setup_guide ?? null,
    isLoading: setupGuideQuery.isLoading,
  };
}

export const REQUIRED_PEOPLE_FOR_ACTIVATION = 40;

export function useHasEnoughPeople(setupGuide: SetupGuide | null) {
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  const needsReconciling = setupGuide != null && setupGuide.completed_at == null;
  const integrationStatus = useIntegrationStatusOptional({ workspaceId, enabled: needsReconciling });

  return (integrationStatus.data?.customers ?? 0) >= REQUIRED_PEOPLE_FOR_ACTIVATION;
}

export const ACTION_CHANNELS = ['twilio', 'slack', 'in_app', 'push', 'inbox', 'line', 'whatsapp'];

const DEMO_ACCOUNT_IDS = [31505, 61962];

type WorkspaceSetupCompleteInput = {
  setupGuide: SetupGuide;
  hasEnoughPeople: boolean;
  enabledActions: readonly string[] | null | undefined;
  isEmailSetupComplete: boolean;
};

/** Exact port of Ember's `adventures.isWorkspaceSetupComplete`. */
export function isWorkspaceSetupComplete({
  setupGuide,
  hasEnoughPeople,
  enabledActions,
  isEmailSetupComplete,
}: WorkspaceSetupCompleteInput): boolean {
  if (setupGuide.completed_at != null) {
    return true;
  }

  const isActionChannelComplete = ACTION_CHANNELS.some((channel) => enabledActions?.includes(channel) === true);
  const isIntegrationComplete = setupGuide.skip_integration_step === true || hasEnoughPeople;

  return isIntegrationComplete && (isEmailSetupComplete || isActionChannelComplete);
}

function isDemoAccount(accountId: number | null | undefined): boolean {
  return accountId != null && DEMO_ACCOUNT_IDS.includes(accountId);
}

type WorkspaceSetupStatusInput = {
  accountId: number | null | undefined;
  accountVerificationState: string | undefined;
  workspaceId: number | null;
  setupGuide: SetupGuide | null;
  setupGuideIsLoading: boolean;
  setupGuideIsError: boolean;
  setupGuideIsSuccess: boolean;
  environment: WorkspaceEnvironment | undefined;
  environmentIsLoading: boolean;
  environmentIsError: boolean;
  domains: DomainsList | undefined;
  domainsIsLoading: boolean;
  domainsIsError: boolean;
  customers: number | undefined;
  integrationIsLoading: boolean;
  integrationIsError: boolean;
  sampleIsLoading: boolean;
  sampleIsError: boolean;
  isSampleWorkspace: (workspaceId: number | null | undefined) => boolean;
};

function needsLiveSetupState(setupGuide: SetupGuide | null): boolean {
  return setupGuide != null && setupGuide.completed_at == null && setupGuide.version !== 1;
}

function isSetupStatusLoading(input: WorkspaceSetupStatusInput, needsLiveState: boolean): boolean {
  return (
    input.setupGuideIsLoading ||
    input.sampleIsLoading ||
    (needsLiveState && (input.environmentIsLoading || input.domainsIsLoading || input.integrationIsLoading))
  );
}

function isSetupStatusError(input: WorkspaceSetupStatusInput, needsLiveState: boolean): boolean {
  return (
    input.setupGuideIsError ||
    input.sampleIsError ||
    (needsLiveState && (input.environmentIsError || input.domainsIsError || input.integrationIsError))
  );
}

function isEmailSetupComplete(input: WorkspaceSetupStatusInput): boolean {
  return (
    input.accountId != null &&
    input.accountVerificationState === VerificationState.Approved &&
    input.environment?.identities?.some((identity) => identity?.template_type === 'email') === true &&
    input.domains?.domains?.some((domain) => domain.verified === true) === true
  );
}

function isSetupComplete(input: WorkspaceSetupStatusInput, isReady: boolean, emailSetupComplete: boolean): boolean {
  if (isStoredSetupComplete(input.setupGuide)) {
    return true;
  }
  if (!isReady || input.setupGuide == null) {
    return false;
  }

  return isWorkspaceSetupComplete({
    setupGuide: input.setupGuide,
    hasEnoughPeople: (input.customers ?? 0) >= REQUIRED_PEOPLE_FOR_ACTIVATION,
    enabledActions: input.environment?.environment?.enabled_actions ?? [],
    isEmailSetupComplete: emailSetupComplete,
  });
}

function isStoredSetupComplete(setupGuide: SetupGuide | null): boolean {
  return setupGuide?.completed_at != null || setupGuide?.version === 1;
}

function getWorkspaceSetupStatus(input: WorkspaceSetupStatusInput) {
  const needsLiveState = needsLiveSetupState(input.setupGuide);
  const isLoading = isSetupStatusLoading(input, needsLiveState);
  const isError = isSetupStatusError(input, needsLiveState);
  const isReady = input.setupGuideIsSuccess && !isLoading && !isError;
  const emailSetupComplete = isEmailSetupComplete(input);

  return {
    setupGuide: input.setupGuide,
    isComplete: isSetupComplete(input, isReady, emailSetupComplete),
    isDemoAccount: isDemoAccount(input.accountId),
    isLoading,
    isError,
    isReady,
    isSampleWorkspace: input.isSampleWorkspace(input.workspaceId),
  };
}

export function useWorkspaceSetupStatus() {
  const { account } = useAuth();
  const { setupGuideQuery, workspaceId } = useSetupGuide();
  const setupGuide = setupGuideQuery.data?.setup_guide ?? null;
  const needsLiveState = needsLiveSetupState(setupGuide);
  const environmentQuery = useEnvironmentOptional(workspaceId ?? undefined, { enabled: needsLiveState });
  const domainsQuery = useDomainsOptional<DomainsList>({
    workspaceIdOverride: workspaceId ?? undefined,
    enabled: needsLiveState,
  });
  const integrationStatusQuery = useIntegrationStatusOptional({ workspaceId, enabled: needsLiveState });
  const sampleWorkspace = useSampleWorkspace();

  return getWorkspaceSetupStatus({
    accountId: account?.id,
    accountVerificationState: account?.verification_state,
    workspaceId,
    setupGuide,
    setupGuideIsLoading: setupGuideQuery.isLoading,
    setupGuideIsError: setupGuideQuery.isError,
    setupGuideIsSuccess: setupGuideQuery.isSuccess,
    environment: environmentQuery.data,
    environmentIsLoading: environmentQuery.isLoading,
    environmentIsError: environmentQuery.isError,
    domains: domainsQuery.data,
    domainsIsLoading: domainsQuery.isLoading,
    domainsIsError: domainsQuery.isError,
    customers: integrationStatusQuery.data?.customers,
    integrationIsLoading: integrationStatusQuery.isLoading,
    integrationIsError: integrationStatusQuery.isError,
    sampleIsLoading: sampleWorkspace.isLoading,
    sampleIsError: sampleWorkspace.isError,
    isSampleWorkspace: sampleWorkspace.isSampleWorkspace,
  });
}

/**
 * Whether a messaging action channel counts as set up.
 *
 * SMS is provisioned through the Custom SMS vendor settings, which surface as
 * the `sms` enabled action. Twilio is still offered there as a temporary
 * option, so either action marks the SMS channel done.
 *
 * The setup-guide reconcile intentionally recognizes more action channels than the five
 * currently shown by the workspace-setup card. This matches Ember's `adventures` service,
 * whose completion predicate includes `inbox` and `line` as well.
 */
export function isActionChannelComplete(
  channelId: string,
  enabledActions: readonly string[] | null | undefined,
): boolean {
  if (channelId === 'twilio') {
    return enabledActions?.includes('sms') === true || enabledActions?.includes('twilio') === true;
  }
  return enabledActions?.includes(channelId) === true;
}

type SetupGuidePatchInput = {
  setupGuide: SetupGuide;
  environmentData: WorkspaceEnvironment | undefined;
  domainsData: DomainsList | undefined;
  hasEnoughPeople: boolean;
  now: number;
};

function isEmailStepComplete(environmentData: WorkspaceEnvironment, domainsData: DomainsList): boolean {
  const verifiedDomains = (domainsData.domains ?? []).filter((domain) => isSendingVerified(domain));
  const emailIdentities = (environmentData.identities ?? []).filter((identity) => identity?.template_type === 'email');

  return (
    verifiedDomains.length > 0 &&
    emailIdentities.length > 0 &&
    environmentData.environment != null &&
    environmentData.environment.delivery_settings != null &&
    environmentData.environment.delivery_settings !== DeliverySettings.SendToOne
  );
}

function isLiveSetupComplete(input: SetupGuidePatchInput): boolean {
  if (input.environmentData == null || input.domainsData == null || input.environmentData.environment == null) {
    return false;
  }

  const environment = input.environmentData.environment;
  return (
    (isEmailStepComplete(input.environmentData, input.domainsData) ||
      input.setupGuide.skip_email_channel_setup === true) &&
    isMessagingStepComplete(input.setupGuide, environment.enabled_actions) &&
    isIntegrationStepComplete(input)
  );
}

function isMessagingStepComplete(
  setupGuide: SetupGuide,
  enabledActions: readonly string[] | null | undefined,
): boolean {
  return (
    ACTION_CHANNELS.some((channel) => isActionChannelComplete(channel, enabledActions ?? [])) ||
    setupGuide.skip_messaging_channels_setup === true
  );
}

function isIntegrationStepComplete(input: SetupGuidePatchInput): boolean {
  return input.hasEnoughPeople || input.setupGuide.skip_integration_step === true;
}

function getLiveSetupGuidePatches({
  setupGuide,
  environmentData,
  domainsData,
  hasEnoughPeople,
  now,
}: SetupGuidePatchInput): Partial<SetupGuide> | null {
  if (environmentData == null || domainsData == null || environmentData.environment == null) {
    return null;
  }

  const patches: Partial<SetupGuide> =
    hasEnoughPeople && setupGuide.required_people_added_at == null ? { required_people_added_at: now } : {};

  if (isLiveSetupComplete({ setupGuide, environmentData, domainsData, hasEnoughPeople, now })) {
    patches.completed_at = now;
  }

  return patches;
}

function getSetupGuidePatches(input: SetupGuidePatchInput): Partial<SetupGuide> | null {
  return input.setupGuide.version === 1 ? { completed_at: input.now } : getLiveSetupGuidePatches(input);
}

type ShouldReconcileInput = {
  setupGuide: SetupGuide | null;
  workspaceId: number | null;
  urlWorkspaceId: number | null;
  isSampleWorkspaceLoading: boolean;
  isSampleWorkspaceError: boolean;
  isSample: boolean;
};

type ReadyShouldReconcileInput = Omit<ShouldReconcileInput, 'setupGuide' | 'workspaceId'> & {
  setupGuide: SetupGuide;
  workspaceId: number;
};

/** The input with its guide and workspace present, or null while there is nothing to reconcile yet. */
function readyToReconcile(input: ShouldReconcileInput): ReadyShouldReconcileInput | null {
  const { setupGuide, workspaceId } = input;
  if (
    setupGuide == null ||
    setupGuide.completed_at != null ||
    workspaceId == null ||
    workspaceId !== input.urlWorkspaceId ||
    input.isSampleWorkspaceLoading ||
    input.isSampleWorkspaceError ||
    input.isSample
  ) {
    return null;
  }
  return { ...input, setupGuide, workspaceId };
}

/**
 * Reconciles the setup guide blob with live system state.
 *
 * The setup guide stores completion timestamps that are normally written by
 * the Ember app when a user completes steps through the setup flow. But when
 * steps are completed outside the flow (e.g. Track API integration, settings
 * pages), those timestamps never get written and users get stuck on the setup
 * screen forever.
 *
 * This hook checks live state and backfills missing timestamps, including
 * stamping `completed_at` when all steps are satisfied.
 */
export function useReconcileSetupGuide(setupGuide: SetupGuide | null, hasEnoughPeople: boolean) {
  // The guide and write use the resolved workspace, while evidence queries use the URL workspace.
  // They must agree or a `/workspaces/last` visit could stamp the wrong guide (FPS-4144).
  const { workspaceId } = useSetupGuide();
  const urlWorkspaceId = useWorkspaceIdFromCurrentUrl();
  const invalidateSetupGuide = useInvalidateSetupGuide();
  const isReconciling = useRef(false);

  const needsReconciling = setupGuide != null && setupGuide.completed_at == null;
  const needsLiveState = needsReconciling && setupGuide?.version !== 1;
  const { data: environmentData } = useEnvironmentOptional(undefined, { enabled: needsLiveState });
  const { data: domainsData } = useDomainsOptional<DomainsList>({ enabled: needsLiveState });
  const {
    isSampleWorkspace,
    isLoading: isSampleWorkspaceLoading,
    isError: isSampleWorkspaceError,
  } = useSampleWorkspace();
  const isSample = isSampleWorkspace(workspaceId ?? undefined);

  useEffect(() => {
    const reconcileInput = {
      setupGuide,
      workspaceId,
      urlWorkspaceId,
      isSampleWorkspaceLoading,
      isSampleWorkspaceError,
      isSample,
    };
    const ready = readyToReconcile(reconcileInput);
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
    if (ready == null) return;
    if (isReconciling.current) return;
    const now = Math.floor(Date.now() / 1000);
    const patches = getSetupGuidePatches({
      setupGuide: ready.setupGuide,
      environmentData,
      domainsData,
      hasEnoughPeople,
      now,
    });

    if (patches == null || Object.keys(patches).length === 0) return;

    isReconciling.current = true;
    void updateSetupGuide(ready.workspaceId, { ...ready.setupGuide, ...patches })
      .then(() => {
        void invalidateSetupGuide();
      })
      .catch((error: unknown) => {
        // Best-effort backfill — log but don't surface to the user. The hook
        // will retry on the next dependency change or remount.
        // oxlint-disable-next-line no-console -- best-effort setup-guide backfill; the failure is intentionally non-blocking
        console.warn('Failed to reconcile setup guide:', error);
      })
      .finally(() => {
        isReconciling.current = false;
      });
  }, [
    setupGuide,
    workspaceId,
    urlWorkspaceId,
    environmentData,
    domainsData,
    hasEnoughPeople,
    isSample,
    isSampleWorkspaceError,
    isSampleWorkspaceLoading,
    invalidateSetupGuide,
  ]);
}
